import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import {
  familyAvatarUploadUrl,
  removeMember,
  renameFamily,
  setFamilyAvatar,
  transferOwnership,
  updateMemberPermissions,
  type FamilyMember,
  type FamilyResponse,
  type FriendInviteMode,
  type OwnerDisposition,
} from "@/api/families";
import {
  getOwnershipTransferPreview,
  tierName,
  type DowngradeImpact,
} from "@/api/billing";
import { extractApiError } from "@/api/client";
import {
  familiesQuery,
  invitesQuery,
  subscriptionQuery,
} from "@/lib/queries";
import { countLabel } from "@/lib/format";
import { s3 } from "@/api/archive";
import {
  Avatar,
  Card,
  Chip,
  ErrorText,
  Hairline,
  InkButton,
  LoadingBlock,
  QuietButton,
  RadioRow,
  SectionLabel,
} from "@/components/ui";
import { Modal } from "@/components/Modal";
import { InviteDialog, PendingInvitesSection } from "@/components/invites";
import { LeaveCircleWizard } from "@/components/LeaveCircleWizard";

/**
 * Immediate Family Circle — mirrors iOS CircleScreen (the locked
 * source of truth for labels, flows, and gating):
 * members list with role/minor/relationship badges, owner rename,
 * invite via share link, pending invites, remove-member with the two
 * dispositions, leave (non-owner), transfer ownership (owner), and
 * the 1-seat-plan upgrade explainer instead of dead-end affordances.
 */

const roleLabel: Record<FamilyMember["role"], string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
};

const CirclePage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  return (
    <AppShell user={user}>
      <CircleView user={user} />
    </AppShell>
  );
};

const CircleView = ({ user }: { user: UserResponse }) => {
  const queryClient = useQueryClient();
  const families = useQuery(familiesQuery);
  const home = families.data?.find(
    (f) => f.family_id === user.home_family_id,
  );
  const invites = useQuery({
    ...invitesQuery(user.home_family_id),
    enabled: !!home,
  });
  const subscription = useQuery(subscriptionQuery());

  if (families.isPending) {
    return <LoadingBlock label="Loading your circle…" />;
  }
  if (families.isError || !home) {
    return (
      <div className="mx-auto max-w-xl py-10 text-center">
        <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-blush">
          Couldn't load
        </p>
        <p className="mt-3 font-serif text-[15px] italic text-ink-soft">
          We couldn't load your circle. Please try again.
        </p>
        <div className="mt-5">
          <QuietButton onClick={() => families.refetch()}>
            Try again
          </QuietButton>
        </div>
      </div>
    );
  }

  const isOwner =
    home.members.find((m) => m.user_id === user.user_id)?.role === "owner";
  const seatsCap = subscription.data?.entitlements.seats.cap ?? null;
  const soloSeatCap = seatsCap === 1;
  const canInvite = home.my_permissions.invite_ifc && !soloSeatCap;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["families"] });
    void queryClient.invalidateQueries({
      queryKey: ["invites", home.family_id],
    });
    void queryClient.invalidateQueries({ queryKey: ["family-links"] });
    void queryClient.invalidateQueries({ queryKey: ["subscription"] });
  };

  return (
    <div className="mx-auto max-w-2xl">
      <CircleHeader home={home} isOwner={isOwner} />

      <section className="mt-8">
        <SectionLabel>People in your circle</SectionLabel>
        <MembersCard
          home={home}
          isOwner={isOwner}
          currentUserId={user.user_id}
          onChanged={refresh}
        />
      </section>

      {canInvite && (
        <div className="mt-6">
          <InviteSomeone
            home={home}
            isOwner={isOwner}
            seatsUsed={subscription.data?.entitlements.seats.used ?? null}
            seatsCap={seatsCap}
            onChanged={refresh}
          />
        </div>
      )}

      {isOwner && soloSeatCap && home.members.length === 1 && (
        <Card className="mt-6 p-6">
          <p className="font-serif text-[15px] italic leading-[24px] text-ink-soft">
            You're on the {tierName(home.subscription.tier)} plan, which
            includes 1 member in your Immediate Family Circle. If your
            immediate family needs to expand, you can upgrade your plan at
            any time.
          </p>
        </Card>
      )}

      <PendingInvitesSection
        familyId={home.family_id}
        invites={invites.data ?? []}
        intent="join_ifc"
        canManage={home.my_permissions.invite_ifc}
        canResend={!soloSeatCap}
        currentUserId={user.user_id}
        isOwner={isOwner}
        onChanged={refresh}
      />

      <GovernanceSection
        home={home}
        isOwner={isOwner}
        currentUserId={user.user_id}
        onChanged={refresh}
      />
    </div>
  );
};

// ── Header: avatar + rename + tier line ────────────────────────────

const CircleHeader = ({
  home,
  isOwner,
}: {
  home: FamilyResponse;
  isOwner: boolean;
}) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(home.name);
  const [error, setError] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: () => renameFamily(home.family_id, name.trim()),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["families"] });
    },
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't save the new name. Please try again.",
      ),
  });

  // P-CI.5 — owner-editable family avatar (iOS AvatarPhotoBlock):
  // presign → PUT the bytes → PATCH avatar_url → refetch.
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const uploadAvatar = async (file: File) => {
    setUploadingAvatar(true);
    setError(null);
    try {
      const { upload_url, public_url } = await familyAvatarUploadUrl(
        home.family_id,
        { type: file.type, size: file.size },
      );
      await s3.put(upload_url, file, {
        headers: { "Content-Type": file.type },
      });
      await setFamilyAvatar(home.family_id, public_url);
      void queryClient.invalidateQueries({ queryKey: ["families"] });
    } catch {
      setError("Couldn't update the photo. Please try again.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <header className="flex items-center gap-5">
      <div className="relative">
        <Avatar name={home.name} url={home.avatar_url} size={64} />
        {isOwner && (
          <label
            aria-label="Change your circle's photo"
            className="tap-target absolute -bottom-1 -right-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-ink text-white shadow-md ring-2 ring-white hover:bg-ink/90"
          >
            {uploadingAvatar ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden />
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              disabled={uploadingAvatar}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void uploadAvatar(file);
              }}
            />
          </label>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {editing ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) rename.mutate();
            }}
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
              aria-label="Circle name"
              placeholder="Circle name"
              className="tap-target w-full max-w-sm rounded-2xl border border-sand bg-white px-4 py-2 font-serif text-[20px] font-medium text-ink outline-none focus:border-taupe"
            />
            <InkButton type="submit" disabled={!name.trim() || rename.isPending}>
              {rename.isPending ? "Saving…" : "Save"}
            </InkButton>
            <QuietButton
              onClick={() => {
                setEditing(false);
                setName(home.name);
              }}
            >
              Cancel
            </QuietButton>
          </form>
        ) : (
          <div className="flex items-center gap-2">
            <h1
              className="truncate font-serif text-[28px] leading-[34px] text-ink"
              style={{ fontWeight: 600 }}
            >
              {home.name}
            </h1>
            {isOwner && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                aria-label="Rename your circle"
                className="tap-target flex h-11 w-11 items-center justify-center rounded-full text-taupe-deep hover:bg-white"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1z" />
                </svg>
              </button>
            )}
          </div>
        )}
        <p className="mt-1 font-sans text-[13px] text-ink-soft">
          {tierName(home.subscription.tier)} tier ·{" "}
          {countLabel(home.members.length, "person", "people")}
        </p>
        <ErrorText>{error}</ErrorText>
      </div>
    </header>
  );
};

// ── Members list + remove flow ──────────────────────────────────────

const MembersCard = ({
  home,
  isOwner,
  currentUserId,
  onChanged,
}: {
  home: FamilyResponse;
  isOwner: boolean;
  currentUserId: string;
  onChanged: () => void;
}) => {
  const [inspecting, setInspecting] = useState<FamilyMember | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  return (
    <>
      <Card>
        {home.members.map((m, i) => {
          // iOS rule: owner inspects everyone but themselves and the
          // owner row (Law 4 — structurally non-removable).
          const inspectable =
            isOwner && m.role !== "owner" && m.user_id !== currentUserId;
          const row = (
            <div className="flex items-center gap-4 px-5 py-4">
              <Avatar name={m.display_name} url={m.avatar_url} size={40} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-sans text-[15px] font-medium text-ink">
                  {m.display_name}
                  {m.user_id === currentUserId && (
                    <span className="text-ink-soft"> (you)</span>
                  )}
                </p>
                <p className="font-sans text-[13px] text-ink-soft">
                  {roleLabel[m.role]}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {relationshipBadge(m.relationship) && (
                  <Chip>{relationshipBadge(m.relationship)}</Chip>
                )}
                {m.is_minor && <Chip>Minor</Chip>}
                {m.role === "owner" && <Chip>Owner</Chip>}
                {inspectable && (
                  <QuietButton
                    onClick={() => setInspecting(m)}
                    ariaLabel={`Manage ${m.display_name}`}
                  >
                    Manage
                  </QuietButton>
                )}
              </div>
            </div>
          );
          return (
            <div key={m.user_id}>
              {i > 0 && <Hairline />}
              {row}
            </div>
          );
        })}
      </Card>

      {banner && (
        <Card className="mt-4 p-5">
          <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
            {banner}
          </p>
          <div className="mt-3">
            <QuietButton onClick={() => setBanner(null)}>Okay</QuietButton>
          </div>
        </Card>
      )}

      {inspecting && (
        <MemberInspectDialog
          home={home}
          member={inspecting}
          onClose={() => setInspecting(null)}
          onChanged={onChanged}
          onRemoved={(msg) => {
            setInspecting(null);
            setBanner(msg);
            onChanged();
          }}
        />
      )}
    </>
  );
};

/** "son"/"daughter" → badge text; unknown wire values stay hidden
 * (iOS `relationshipLabel` rule — never leak raw enum strings). */
const relationshipBadge = (rel: string | null): string | null => {
  if (rel === "son") return "Son";
  if (rel === "daughter") return "Daughter";
  return null;
};

// ── Member inspect — PERMISSIONS (default) + REMOVAL tabs ──────────
// The web replica of iOS RemoveMemberDrawer (D-IP.9): the member
// card the invite drawer's "You can change these later." promises.

const MemberInspectDialog = ({
  home,
  member,
  onClose,
  onChanged,
  onRemoved,
}: {
  home: FamilyResponse;
  member: FamilyMember;
  onClose: () => void;
  onChanged: () => void;
  onRemoved: (banner: string) => void;
}) => {
  const [tab, setTab] = useState<"permissions" | "removal">("permissions");
  return (
    <Modal title={member.display_name} onClose={onClose}>
      <div className="flex gap-2" role="tablist">
        {(
          [
            ["permissions", "Permissions"],
            ["removal", "Removal"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`tap-target rounded-full px-4 py-1.5 font-sans text-[13px] font-medium transition-colors ${
              tab === key
                ? "bg-ink text-white"
                : "border border-sand text-ink hover:border-taupe"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="mt-5">
        {tab === "permissions" ? (
          <PermissionsTab home={home} member={member} onChanged={onChanged} />
        ) : (
          <RemovalTab home={home} member={member} onDone={onRemoved} />
        )}
      </div>
    </Modal>
  );
};

const PermissionsTab = ({
  home,
  member,
  onChanged,
}: {
  home: FamilyResponse;
  member: FamilyMember;
  onChanged: () => void;
}) => {
  const [inviteIfc, setInviteIfc] = useState(member.permissions.invite_ifc);
  const [inviteEfc, setInviteEfc] = useState(member.permissions.invite_efc);
  const [friends, setFriends] = useState<FriendInviteMode>(
    member.permissions.friend_invites,
  );
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    inviteIfc !== member.permissions.invite_ifc ||
    inviteEfc !== member.permissions.invite_efc ||
    friends !== member.permissions.friend_invites;

  const save = useMutation({
    mutationFn: () =>
      updateMemberPermissions(home.family_id, member.user_id, {
        invite_ifc: inviteIfc,
        invite_efc: inviteEfc,
        friend_invites: friends,
      }),
    onSuccess: () => {
      setSaved(true);
      setError(null);
      onChanged();
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't save those permissions. Please try again.",
      ),
  });

  return (
    <div>
      <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
        What {member.display_name} can do in your circle.
      </p>
      <div className="mt-4">
        <PermissionToggle
          label="Can invite family members"
          checked={inviteIfc}
          onToggle={() => setInviteIfc(!inviteIfc)}
        />
        <Hairline />
        <PermissionToggle
          label="Can connect extended family"
          checked={inviteEfc}
          onToggle={() => setInviteEfc(!inviteEfc)}
        />
        <Hairline />
        <div className="py-3">
          <span className="font-sans text-[14px] font-medium text-ink">
            Friend invites
          </span>
          <div className="mt-2 flex flex-wrap gap-2">
            {(
              [
                ["none", "None"],
                ["approved", "Needs approval"],
                ["any", "Anyone"],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                aria-pressed={friends === mode}
                onClick={() => setFriends(mode)}
                className={`tap-target rounded-full px-4 py-1.5 font-sans text-[13px] font-medium transition-colors ${
                  friends === mode
                    ? "bg-ink text-white"
                    : "border border-sand text-ink hover:border-taupe"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ErrorText>{error}</ErrorText>
      <div className="mt-5 flex items-center justify-end gap-3">
        {saved && (
          <span className="font-sans text-[13px] text-ink-soft">Saved</span>
        )}
        <InkButton
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </InkButton>
      </div>
    </div>
  );
};

const PermissionToggle = ({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) => (
  <label className="tap-target flex cursor-pointer items-center justify-between gap-3 py-3">
    <span className="font-sans text-[14px] text-ink">{label}</span>
    <input
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      className="h-5 w-5 accent-[#323232]"
    />
  </label>
);

const RemovalTab = ({
  home,
  member,
  onDone,
}: {
  home: FamilyResponse;
  member: FamilyMember;
  onDone: (banner: string) => void;
}) => {
  const [disposition, setDisposition] = useState<OwnerDisposition>("keep");
  const [keepLink, setKeepLink] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () =>
      removeMember(home.family_id, {
        user_id: member.user_id,
        owner_disposition: disposition,
        keep_link: keepLink,
      }),
    onSuccess: (res) => {
      // Result copy verbatim from the iOS removal banner.
      const posts =
        res.owner_disposition === "keep"
          ? "Their past posts remain in your feed."
          : "Their posts have moved to their archive.";
      const connection = res.link_id
        ? "Your circles are now linked as Extended Family."
        : res.keep_link
          ? "Couldn't link your circles automatically — send a fresh Extended Family invite if you both want to stay connected."
          : "";
      onDone(
        `${member.display_name} is now in their own Immediate Family Circle. ${posts} ${connection}`.trim(),
      );
    },
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't remove them right now. Please try again.",
      ),
  });

  return (
    <div>
      <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
        They'll start their own Immediate Family Circle. You choose what
        happens to the memories they've shared here.
      </p>

      <div className="mt-5">
        <SectionLabel>Their memories in your feed</SectionLabel>
        <Card className="overflow-hidden border border-sand/40">
          <div role="radiogroup" aria-label="Their memories in your feed">
            <RadioRow
              label="Keep them"
              sub="Their past posts remain in your feed."
              selected={disposition === "keep"}
              onSelect={() => setDisposition("keep")}
            />
            <Hairline />
            <RadioRow
              label="Remove them"
              sub="Their posts move to their own archive."
              selected={disposition === "remove"}
              onSelect={() => setDisposition("remove")}
            />
          </div>
        </Card>
      </div>

      <label className="tap-target mt-5 flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={keepLink}
          onChange={(e) => setKeepLink(e.target.checked)}
          className="mt-1 h-5 w-5 accent-[#8A7460]"
        />
        <span>
          <span className="block font-sans text-[15px] font-medium text-ink">
            Stay connected
          </span>
          <span className="block font-sans text-[13px] text-ink-soft">
            Link your circles as Extended Family so memories can still flow
            between you.
          </span>
        </span>
      </label>

      <ErrorText>{error}</ErrorText>

      <div className="mt-6 flex justify-end">
        <InkButton onClick={() => remove.mutate()} disabled={remove.isPending}>
          {remove.isPending ? "Removing…" : `Remove ${member.display_name}`}
        </InkButton>
      </div>
    </div>
  );
};

// ── Invite ──────────────────────────────────────────────────────────

const InviteSomeone = ({
  home,
  isOwner,
  seatsUsed,
  seatsCap,
  onChanged,
}: {
  home: FamilyResponse;
  isOwner: boolean;
  seatsUsed: number | null;
  seatsCap: number | null;
  onChanged: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  // iOS seat-meter doorway (P-CI.3): at a full roster the button
  // stays visible (locked-state taps convert) but routes to the
  // upgrade explainer — the D-CI.C stub until the plans surface.
  const atCap =
    seatsUsed !== null && seatsCap !== null && seatsUsed >= seatsCap;
  return (
    <>
      <InkButton onClick={() => (atCap ? setGateOpen(true) : setOpen(true))}>
        Invite someone
      </InkButton>
      {seatsUsed !== null && seatsCap !== null && (
        <p className="mt-2 font-mono text-[11px] text-ink-soft">
          {seatsUsed} of {countLabel(seatsCap, "member")}
          {atCap && (
            <span className="text-blush"> · Upgrade to add more</span>
          )}
        </p>
      )}
      {open && (
        <InviteDialog
          familyId={home.family_id}
          intent="join_ifc"
          title="Invite someone to your circle"
          minterPermissions={home.my_permissions}
          minterIsOwner={isOwner}
          onClose={() => setOpen(false)}
          onCompleted={onChanged}
        />
      )}
      {gateOpen && (
        <Modal title="Your member seats are full" onClose={() => setGateOpen(false)}>
          <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
            Everyone on your plan has a seat. Add a seat or upgrade your
            plan to invite more of your family.
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <QuietButton onClick={() => setGateOpen(false)}>
              Not now
            </QuietButton>
            <InkButton
              onClick={() => {
                window.location.href = "/plans";
              }}
            >
              See plans
            </InkButton>
          </div>
        </Modal>
      )}
    </>
  );
};

// ── Governance: transfer ownership / leave ─────────────────────────

const GovernanceSection = ({
  home,
  isOwner,
  currentUserId,
  onChanged,
}: {
  home: FamilyResponse;
  isOwner: boolean;
  currentUserId: string;
  onChanged: () => void;
}) => {
  const [transferring, setTransferring] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const adultOthers = home.members.filter(
    (m) => m.user_id !== currentUserId && !m.is_minor,
  );

  return (
    <section className="mt-10">
      {isOwner && adultOthers.length > 0 && (
        <QuietButton onClick={() => setTransferring(true)}>
          Transfer ownership
        </QuietButton>
      )}
      {!isOwner && (
        <div>
          <QuietButton onClick={() => setLeaving(true)}>
            Leave this circle
          </QuietButton>
          <p className="mt-2 font-sans text-[13px] text-ink-soft">
            You'll start your own Immediate Family Circle.
          </p>
        </div>
      )}

      {transferring && (
        <TransferOwnershipDialog
          home={home}
          candidates={adultOthers}
          onClose={() => setTransferring(false)}
          // Refresh only — the dialog stays mounted to show its done
          // screen (iOS parity); Done/close exits via onClose.
          onDone={onChanged}
        />
      )}
      {leaving && (
        <LeaveCircleWizard home={home} onClose={() => setLeaving(false)} />
      )}
    </section>
  );
};

/**
 * Web replica of iOS TransferOwnershipSheet (wired iOS-side
 * 2026-08-18, Option B): candidate picker + the D-OT.5 billing-
 * impact preview (real numbers, never vibes — the plan follows the
 * payer, so the circle drops to the Starter floor unless the new
 * owner subscribes), then a done screen. The preview is ADVISORY:
 * a failed fetch renders the honest fallback line, never a block.
 */
const TransferOwnershipDialog = ({
  home,
  candidates,
  onClose,
  onDone,
}: {
  home: FamilyResponse;
  candidates: FamilyMember[];
  onClose: () => void;
  onDone: () => void;
}) => {
  const [chosen, setChosen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [doneName, setDoneName] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ["ownership-transfer-preview", home.family_id],
    queryFn: () => getOwnershipTransferPreview(home.family_id),
    staleTime: 60_000,
    retry: false,
  });

  const chosenName =
    candidates.find((m) => m.user_id === chosen)?.display_name ?? null;

  const transfer = useMutation({
    mutationFn: () => transferOwnership(home.family_id, chosen!),
    onSuccess: () => {
      // Refresh on SUCCESS, not on the Done tap — every exit from
      // here must re-render against the new roles (iOS audit MED-1).
      onDone();
      setDoneName(chosenName);
    },
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't transfer ownership. Try again, or contact support if this keeps happening.",
      ),
  });

  if (doneName) {
    return (
      <Modal title="Transfer complete" onClose={onClose}>
        <p className="font-serif text-[20px] font-medium leading-[28px] text-ink">
          {doneName} now owns {home.name}
        </p>
        <p className="mt-2 font-serif text-[14px] text-ink-soft">
          You're a member now. Everything you've shared stays right
          where it is.
        </p>
        <div className="mt-6 flex justify-end">
          <InkButton onClick={onClose}>Done</InkButton>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Transfer ownership" onClose={onClose}>
      <p className="font-serif text-[20px] font-medium leading-[28px] text-ink">
        Pass {home.name} to a new owner
      </p>
      <p className="mt-2 font-serif text-[14px] leading-[22px] text-ink-soft">
        The new owner takes over invites, links, children's accounts,
        and the circle's plan. You stay a member — nothing you've
        shared changes, and your subscription stays with you.
      </p>

      <div className="mt-5">
        <SectionLabel>Pick the next owner</SectionLabel>
        {candidates.length === 0 ? (
          <p className="font-sans text-[13px] text-ink-soft">
            Ownership can only pass to an adult member, and there isn't
            one in your circle yet. Invite an adult first, then come
            back here.
          </p>
        ) : (
          <Card className="overflow-hidden border border-sand/40">
            <div role="radiogroup" aria-label="New owner">
              {candidates.map((m, i) => (
                <div key={m.user_id}>
                  {i > 0 && <Hairline />}
                  <RadioRow
                    label={m.display_name}
                    sub="Member"
                    selected={chosen === m.user_id}
                    onSelect={() => setChosen(m.user_id)}
                  />
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {preview.data && !preview.data.safe && (
        <div className="mt-5">
          <SectionLabel>What your circle loses</SectionLabel>
          <p className="font-sans text-[13px] leading-[20px] text-ink">
            Your subscription follows you, not the circle. Unless{" "}
            {chosenName ?? "the new owner"} subscribes, here's what
            changes:
          </p>
          <div className="mt-2 flex flex-col gap-2">
            {preview.data.impacts.map((impact) => (
              <Card
                key={impact.kind}
                className="border border-sand/40 px-4 py-3"
              >
                <p className="font-sans text-[13px] leading-[20px] text-ink">
                  {impactCopy(impact)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      )}
      {preview.isError && (
        <p className="mt-4 font-sans text-[12px] leading-[18px] text-ink-soft">
          Couldn't check what your circle's plan covers right now. If
          the new owner doesn't subscribe, the circle drops to the free
          plan and anything over its limits is hidden until someone
          upgrades.
        </p>
      )}

      <ErrorText>{error}</ErrorText>
      <div className="mt-6 flex justify-end gap-3">
        <QuietButton onClick={onClose} disabled={transfer.isPending}>
          Cancel
        </QuietButton>
        <InkButton
          onClick={() => transfer.mutate()}
          disabled={!chosen || transfer.isPending}
        >
          {transfer.isPending
            ? "Transferring…"
            : chosenName
              ? `Make ${chosenName} the owner`
              : "Transfer ownership"}
        </InkButton>
      </div>
    </Modal>
  );
};

/** Per-kind impact copy — verbatim from the iOS sheet (one voice
 * across surfaces). */
const impactCopy = (impact: DowngradeImpact): string => {
  switch (impact.kind) {
    case "members":
      return impact.cap != null && impact.used != null
        ? `Members: the free plan includes ${countLabel(impact.cap, "member")}. Your circle has ${impact.used} — everyone stays, but no one new can join until the circle upgrades.`
        : "Members: the free plan seats fewer people; no one new can join until the circle upgrades.";
    case "pictures":
      return impact.over != null
        ? `Photos: ${countLabel(impact.over, "photo")} over the free limit will be hidden (never deleted) until the circle upgrades again.`
        : "Photos: anything over the free limit will be hidden (never deleted) until the circle upgrades again.";
    case "videos":
      return impact.over != null
        ? `Videos: ${countLabel(impact.over, "video")} over the free limit will be hidden (never deleted) until the circle upgrades again.`
        : "Videos: anything over the free limit will be hidden (never deleted) until the circle upgrades again.";
    case "family_links":
      return "Extended family: links to other circles pause until the circle upgrades again.";
    default:
      return "Plan features: some of what the circle uses today isn't included in the free plan.";
  }
};


export const Route = createFileRoute("/circle")({
  loader: ({ context }) => requireAuth(context.queryClient),
  component: CirclePage,
});
